import { useState } from "react";
import { toast } from "react-toastify";
import { formatRelative } from "../OrgOverview/format";
import { addGoldenLoan, cloneGoldenLoan, removeGoldenLoan } from "./api";

function GoldenLoanComposer({ env, onAdded, onSuiteQueued }) {
  const [mode, setMode] = useState("existing-dev");
  const [devOrganizationId, setDevOrganizationId] = useState("");
  const [devLoanId, setDevLoanId] = useState("");
  const [prodOrganizationId, setProdOrganizationId] = useState("");
  const [prodLoanId, setProdLoanId] = useState("");
  const [targetDevOrganizationId, setTargetDevOrganizationId] = useState("");
  const [notes, setNotes] = useState("");
  const [submittingAction, setSubmittingAction] = useState("");

  if (env !== "dev") {
    return (
      <p className="icr-muted">
        Golden dataset membership can only be edited in Dev.
      </p>
    );
  }

  const submitExisting = async (event) => {
    event.preventDefault();
    setSubmittingAction("add");
    try {
      const result = await addGoldenLoan(
        {
          organizationId: devOrganizationId.trim(),
          loanId: devLoanId.trim(),
          notes: notes.trim() || undefined,
        },
        env,
      );
      if (!result.success) {
        throw new Error(result.reason || "Could not add golden loan.");
      }
      onAdded(result.loan);
      setDevLoanId("");
      setNotes("");
      toast.success(
        result.created
          ? "Loan added to the golden dataset."
          : "Loan was already in the golden dataset.",
      );
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmittingAction("");
    }
  };

  const submitClone = async (event, runAfterClone) => {
    event.preventDefault();
    const form = event.currentTarget.form || event.currentTarget;
    if (typeof form.reportValidity === "function" && !form.reportValidity()) {
      return;
    }
    setSubmittingAction(runAfterClone ? "clone-run" : "clone");
    try {
      const result = await cloneGoldenLoan(
        {
          prodOrganizationId: prodOrganizationId.trim(),
          prodLoanId: prodLoanId.trim(),
          devOrganizationId: targetDevOrganizationId.trim(),
          notes: notes.trim() || undefined,
          runAfterClone,
        },
        env,
      );
      if (!result.success) {
        throw new Error(result.reason || "Could not clone the Prod loan.");
      }
      onAdded(result.loan);
      if (result.suiteRunId) {
        onSuiteQueued(result);
      }
      setProdLoanId("");
      setNotes("");
      toast.success(
        runAfterClone
          ? "Clone, golden curation, and evaluation queued."
          : "Prod clone and golden curation queued.",
      );
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmittingAction("");
    }
  };

  return (
    <>
      <div className="icr-mode-switch" role="tablist" aria-label="Golden loan source">
        <button
          type="button"
          role="tab"
          aria-selected={mode === "existing-dev"}
          className={mode === "existing-dev" ? "is-active" : ""}
          onClick={() => setMode("existing-dev")}
        >
          Existing Dev loan
          <small>Add an existing loan to the dataset</small>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === "clone-prod"}
          className={mode === "clone-prod" ? "is-active" : ""}
          onClick={() => setMode("clone-prod")}
        >
          Clone from Prod
          <small>Copy safely into Dev, then curate</small>
        </button>
      </div>

      {mode === "existing-dev" ? (
        <form className="icr-queue-form icr-golden-add-form" onSubmit={submitExisting}>
          <label>
            Dev Organization ID
            <input
              required
              value={devOrganizationId}
              onChange={(event) => setDevOrganizationId(event.target.value)}
              placeholder="Organization ID"
            />
          </label>
          <label>
            Dev Loan ID
            <input
              required
              value={devLoanId}
              onChange={(event) => setDevLoanId(event.target.value)}
              placeholder="Loan ID"
            />
          </label>
          <label>
            Notes (optional)
            <input
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Why this loan belongs in the suite"
            />
          </label>
          <button
            className="icr-btn icr-btn--primary icr-queue-form__submit"
            disabled={Boolean(submittingAction)}
            type="submit"
          >
            {submittingAction ? "Adding…" : "Add to golden dataset"}
          </button>
        </form>
      ) : (
        <form
          className="icr-queue-form icr-golden-clone-form"
          onSubmit={(event) => submitClone(event, false)}
        >
          <label>
            Prod Organization ID
            <input
              required
              value={prodOrganizationId}
              onChange={(event) => setProdOrganizationId(event.target.value)}
              placeholder="Source organization"
            />
          </label>
          <label>
            Prod Loan ID
            <input
              required
              value={prodLoanId}
              onChange={(event) => setProdLoanId(event.target.value)}
              placeholder="Source loan"
            />
          </label>
          <label>
            Target Dev Organization ID
            <input
              required
              value={targetDevOrganizationId}
              onChange={(event) => setTargetDevOrganizationId(event.target.value)}
              placeholder="Destination organization"
            />
          </label>
          <label>
            Notes (optional)
            <input
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Why this loan belongs in the suite"
            />
          </label>
          <div className="icr-golden-clone-actions">
            <button
              className="icr-btn"
              disabled={Boolean(submittingAction)}
              type="submit"
            >
              {submittingAction === "clone" ? "Cloning…" : "Clone and add"}
            </button>
            <button
              className="icr-btn icr-btn--primary"
              disabled={Boolean(submittingAction)}
              type="button"
              onClick={(event) => submitClone(event, true)}
            >
              {submittingAction === "clone-run"
                ? "Cloning…"
                : "Clone, add, and run"}
            </button>
          </div>
        </form>
      )}
    </>
  );
}

export default function GoldenDatasetCuration({
  env,
  loans,
  loading,
  selectedId,
  selectedIds,
  running,
  onSelect,
  onToggleSelected,
  onAdded,
  onRemoved,
  onRunAll,
  onRunSelected,
  onSuiteQueued,
  onRefresh,
}) {
  const remove = async (goldenLoanId) => {
    try {
      const result = await removeGoldenLoan(goldenLoanId, env);
      if (!result.success) {
        throw new Error(result.reason || "Could not remove golden loan.");
      }
      onRemoved(goldenLoanId);
      toast.success("Removed from golden dataset.");
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <section className="icr-eval-section" aria-labelledby="golden-curation-title">
      <div className="icr-eval-section__header">
        <div>
          <span className="icr-eval-eyebrow">Dataset</span>
          <h2 id="golden-curation-title">Curate golden loans</h2>
          <p>
            Add existing Dev loans or clone safely from Prod. Select any subset
            for a focused suite run.
          </p>
        </div>
        <div className="icr-golden-actions">
          <button
            className="icr-btn"
            type="button"
            disabled={loading}
            onClick={onRefresh}
          >
            {loading ? "Refreshing…" : "Refresh dataset"}
          </button>
          <button
            className="icr-btn icr-btn--primary"
            type="button"
            disabled={running || loading || loans.length === 0 || env !== "dev"}
            onClick={onRunAll}
          >
            {running ? "Queuing…" : "Run all golden loans"}
          </button>
          <button
            className="icr-btn"
            type="button"
            disabled={
              running || loading || selectedIds.length === 0 || env !== "dev"
            }
            onClick={onRunSelected}
          >
            Run selected ({selectedIds.length})
          </button>
        </div>
      </div>

      <GoldenLoanComposer
        env={env}
        onAdded={onAdded}
        onSuiteQueued={onSuiteQueued}
      />

      <div className="icr-table-scroll icr-golden-curation-table">
        <table className="icr-table">
          <thead>
            <tr>
              <th aria-label="Select" />
              <th>Borrower(s)</th>
              <th>Loan type</th>
              <th>Dev Loan ID</th>
              <th>Organization</th>
              <th>Human truth</th>
              <th>Added</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loans.map((loan) => (
              <tr
                key={loan.id}
                className={
                  loan.id === selectedId ? "icr-row is-selected" : "icr-row"
                }
                onClick={() => onSelect(loan.id)}
              >
                <td onClick={(event) => event.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(loan.id)}
                    onChange={() => onToggleSelected(loan.id)}
                    aria-label={`Select ${loan.loanId}`}
                  />
                </td>
                <td>
                  <span className="icr-borrower-name">
                    {loan.borrowerName || "Borrower unavailable"}
                  </span>
                  {loan.notes && <span className="icr-cell-note">{loan.notes}</span>}
                </td>
                <td>
                  <span className="icr-loan-type">
                    {loan.loanType || "Not provided"}
                  </span>
                </td>
                <td className="icr-mono">{loan.loanId}</td>
                <td className="icr-mono">{loan.organizationId}</td>
                <td>
                  {loan.humanLabel?.components?.length ||
                  loan.humanLabel?.rows?.length ? (
                    <span className="icr-truth-state icr-truth-state--ready">
                      Optional label set
                    </span>
                  ) : (
                    <span className="icr-truth-state">Not provided</span>
                  )}
                </td>
                <td>{formatRelative(loan.addedAt)}</td>
                <td onClick={(event) => event.stopPropagation()}>
                  <button
                    className="icr-btn icr-btn--danger"
                    type="button"
                    disabled={env !== "dev"}
                    onClick={() => remove(loan.id)}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
            {!loading && loans.length === 0 && (
              <tr>
                <td colSpan={8} className="icr-muted">
                  No golden loans yet. Add a Dev loan or clone one from Prod.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
